<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <div v-loading="isLoading" data-qa="game-content-form">
      <div class="content" data-qa="game-content-form-content">
        <rest-form
          v-if="model"
          ref="content-form"
          :model="model"
          :save-endpoint="saveEndpoint"
          :after-save="afterSave"
          @on-error="isLoading = false"
        >
          <div
            v-for="sectionGroup in formFieldSections"
            :key="sectionGroup.groupName"
            :data-qa="
              `game-campaign-accordion-wrapper-${sectionGroup.groupName}`
            "
          >
            <h2
              class="accordion-title"
              :data-qa="
                `game-campaign-accordion-title-${sectionGroup.groupName}`
              "
            >
              {{ sectionGroup.groupName }}
            </h2>

            <game-campaign-accordion
              class="accordion"
              :items="sectionGroup.items"
              :data-qa="`game-campaign-accordion-${sectionGroup.groupName}`"
            >
              <template v-for="item in sectionGroup.items" v-slot:[item.name]>
                <rest-form-item
                  :key="item.name"
                  :prop="item.name"
                  :label="item.title"
                  :data-qa="`form-item-${item.name}`"
                >
                  <redactor
                    v-if="item.type === GameContentFieldTypes.MULTILINE"
                    v-model="model[item.name]"
                    :disabled="isReadOnly"
                    :data-qa="`text-editor-${item.name}`"
                  />
                  <el-input
                    v-else-if="item.type === GameContentFieldTypes.LINE"
                    :disabled="isReadOnly"
                    v-model="model[item.name]"
                    :data-qa="`text-input-${item.name}`"
                  />
                </rest-form-item>
              </template>
            </game-campaign-accordion>
          </div>
        </rest-form>
      </div>

      <div v-if="isCreationMode" class="footer" data-qa="footer-actions">
        <div class="footer-content">
          <el-button
            type="primary"
            @click="saveContent"
            :disabled="isLoading"
            data-qa="next-step-btn"
          >
            {{ $t('Gamification_Save_Continue_To_Assets') }}
            <i class="el-icon-arrow-right el-icon-right"></i>
          </el-button>
        </div>
      </div>
    </div>
  </rest-page>
</template>

<script>
import _ from 'lodash'
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import Redactor from '@/common/components/Redactor'
import GameCampaignAccordion from '@/modules/gamification/components/games-campaigns/GameCampaignAccordion.vue'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import { GameContentFieldTypes } from '@/modules/gamification/enum/GameContentFieldTypesEnum.js'
import RestPage from '@/common/components/rest/RestPage.vue'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'GameContentForm',
  components: {
    RestPage,
    RestForm,
    Redactor,
    GameCampaignAccordion,
    RestFormItem
  },
  props: {
    gameCampaignId: {
      type: Number
    },
    isCompleted: {
      type: Boolean,
      required: true
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      step: GameCreationSubSteps.CONTENT,
      model: {},
      initialStateModel: undefined,
      formFieldSections: [],
      GameContentFieldTypes,
      isEditEndpoint: false
    }
  },
  mounted() {
    this.fetchGameTemplates()
    this.initialStateModel = _.cloneDeep(this.model)
  },
  watch: {
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    saveFlag: {
      handler() {
        this.saveContent(false)
      }
    }
  },
  computed: {
    hasChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    },
    fetchEndpoint() {
      return [this.fetchGameTemplates()]
    },
    saveEndpoint() {
      if (this.isEditEndpoint) {
        return projectEndpoint({
          method: 'put',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/texts`
        })
      }
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/texts`
      })
    }
  },
  methods: {
    fetchGameTemplates() {
      if (!this.gameCampaignId) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/texts`,
        handler: data => {
          if (data.data.length) {
            this.isEditEndpoint = true
          }
          this.fillModelFromResponse(data)
        }
      })
    },

    fillModelFromResponse(textsData) {
      this.formFieldSections = []
      const newModel = {}

      for (const [groupName, templates] of Object.entries(
        textsData.templates
      )) {
        if (!templates || !templates.length) continue

        const sectionItems = templates.map(template => ({
          title: template.label,
          name: template.field,
          type: template.type || GameContentFieldTypes.LINE
        }))

        this.formFieldSections.push({
          groupName,
          title: groupName.toUpperCase(),
          items: sectionItems
        })

        templates.forEach(template => {
          if (textsData.data.length > 0) {
            let data = textsData.data.find(data => data.name === template.field)
            if (data) {
              newModel[template.field] = data.value
              return
            }
          }
          newModel[template.field] = template.default || ''
        })
      }

      this.model = newModel
      this.initialStateModel = _.cloneDeep(this.model)
    },

    updateContent(field, value) {
      this.$set(this.model, field, value)
    },

    saveContent(shouldGoToNextStep = true) {
      if (!this.hasChanges && shouldGoToNextStep) {
        this.$emit('noChangesSubmit', this.step)
        return
      }

      this.isLoading = true
      this.$refs['content-form'].save()
      this.shouldGoToNextStep = shouldGoToNextStep
    },

    afterSave(data, finishLoading) {
      finishLoading()
      this.isLoading = false
      this.initialStateModel = _.cloneDeep(this.model)

      if (this.shouldGoToNextStep) {
        this.$emit('goToNextStep', this.step)
      } else if (this.isCreationMode) {
        EventBus.$emit('updateStep', this.step)
        this.$nextTick(() => {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
      }
    }
  }
}
</script>

<style scoped>
.content {
  padding-bottom: 60px;
}

.accordion-title {
  font-size: 18px;
  font-weight: 600;
  padding: 20px 0;
  text-transform: capitalize;
  color: #333;
}

.accordion {
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e5e5;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}

.footer-content {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

.default-width {
  width: 596px;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/content-assets/GameStepContentForm.vue